<template>
	<div>
		<content-loader :message="$t('loading')" v-if="loading" />
		<div class="row mt-3" v-else-if="notFound">
			<div class="col-8 offset-2">
				<not-found />
			</div>
		</div>

		<page :title="group.name" icon="layer-group" tabs :sidebar-collapsed="sidebarCollapsed" v-else>
			<template v-slot:itemDetail v-if="canManageSettings">
				<div class="mr-auto">

				</div>
				<div>
					<b-dropdown class="text-white" variant="transparent" no-caret left>
						<template v-slot:button-content>
							<font-awesome-icon icon="ellipsis-v" class="timestamp text-white" />
						</template>
						<b-list-group flush>
							<b-dropdown-item-button button @click="edit">Edit Group</b-dropdown-item-button>
							<b-dd-divider />
							<b-dropdown-item-button button @click="activate" v-if="!group.is_active">Activate Group</b-dropdown-item-button>
							<b-dropdown-item-button button @click="deactivate" v-else>Deactivate Group</b-dropdown-item-button>
							<b-dd-divider />
							<b-dropdown-item-button button @click="$bvModal.show('group-delete-modal')">Delete Group</b-dropdown-item-button>
						</b-list-group>
					</b-dropdown>
				</div>
			</template>
			<page-tabs slot="fullwidth" :sidebar-collapsed="sidebarCollapsed">
				<page-tab :to="groupRoute('group-devices')">Devices</page-tab>
				<page-tab :to="groupRoute('group-metrics')">Metrics</page-tab>
				<page-tab :to="groupRoute('group-notifications')">Notifications</page-tab>
				<page-tab :to="groupRoute('group-monitors')">Monitors ({{ group.monitor_count }})</page-tab>
			</page-tabs>
			<router-view @notFound="resourceNotFound" />

			<b-modal
				id="activate-group-modal"
				header-bg-variant="primary"
				header-text-variant="light"
				title="Activate Group"
				:ok-disabled="saving"
				:cancel-disabled="saving"
				:no-close-on-backdrop="saving"
				:no-close-on-esc="saving"
				:hide-header-close="saving"
				@ok="submitActivateGroup"
				@hidden="closeActivate"
			>
				<b-row>
					<b-col cols="12">
						While activating this group, would you like to activate any of the following:
					</b-col>
				</b-row>
				<b-row>
					<b-col cols="12">
						<b-form-checkbox v-model="activateChildGroups" name="childGroups">Child Groups</b-form-checkbox>
						<b-form-checkbox v-model="activateMonitors" name="monitors">Monitors</b-form-checkbox>
						<b-form-checkbox v-model="activateDevices" name="devices">Devices</b-form-checkbox>
					</b-col>
				</b-row>

				<template v-slot:modal-footer="{ ok, cancel }">
					<b-button variant="link" @click="cancel()">{{ $t('modal.cancel') }}</b-button>
					<b-button variant="primary" @click="ok()" :disabled="saving">
						{{ $t('modal.save') }}
						<b-spinner small v-if="saving" />
					</b-button>
				</template>
			</b-modal>

			<b-modal
				id="group-delete-modal"
				header-bg-variant="danger"
				header-text-variant="white"
				title="Delete Group"
				@ok="deleteGroup"
				@hidden="closeDelete"
			>
				<b-row>
					<b-col cols="12">
						This action will delete this group and subgroups including monitors defined within this group and its subgroups.
						The devices in this group and subgroups will become unassigned. If you would like to move these devices to another
						group, please select a group from the dropdown below.
					</b-col>
				</b-row>
				<b-row class="mt-1">
					<b-col cols="12">
						<b-form-select v-model="deleteGroupChoice" :options="deleteGroupChoices" />
					</b-col>
				</b-row>
			</b-modal>

			<add-group-modal
				id="add-edit-group"
				:client="client"
				:group="group"
				:client-groups="clientGroups"
				@submit="loadGroup(group.id)"
			/>
		</page>
	</div>
</template>

<script>
import Page from '../../components/layout/Page';
import PageTabs from '../../components/PageTabs';
import PageTab from '../../components/PageTab';
import ContentLoader from '../../components/ContentLoader';
import ErrorModal from '../../mixins/ErrorModal';
import nprogress from 'nprogress';
import pluralize from 'pluralize';
import AddGroupModal from '../../components/group/AddGroupModal';
import NotFound from '../../components/errors/404Error';

export default {
	components: {
		Page,
		PageTabs,
		PageTab,
		ContentLoader,
		AddGroupModal,
		NotFound
	},

	mixins: [ ErrorModal ],

	mounted () {
		if (!this.canView) {
			this.$router.push('/');
		}
		else {
			this.loadGroup(this.$route.params.groupId);
		}
	},

	watch: {
		$route (to, from) {
			if (from.params.groupId !== to.params.groupId) {
				if (!this.canView) {
					this.$router.push('/');
				}
				else {
					nprogress.start();
					this.loadGroup(to.params.groupId);
				}
			}
		},

		client (to, from) {
			const fromId = from === null ? null : from.id;
			const toId = to === null ? null : to.id;

			if (fromId !== toId) {
				this.loadGroup(this.groupId);
			}
		}
	},

	data () {
		return {
			loading: true,
			activateChildGroups: false,
			activateMonitors: false,
			activateDevices: false,
			saving: false,
			deleteGroupChoice: null,
			notFound: false
		};
	},

	methods: {
		loadGroup (groupId) {
			this.loading = true;
			this.notFound = false;

			this.$store.dispatch('groups/load', groupId)
				.then(data => {
					// not handling data locally
				})
				.catch(e => {
					if (e.includes('No query results')) {
						this.notFound = true;
					}
					else {
						this.showErrorModal(e, 'Error Retrieving Group Data');
					}
				})
				.finally(() => {
					this.loading = false;
					nprogress.done();
				});
		},

		groupRoute (name, params = {}) {
			return {
				name,
				params: {
					...params,
					id: this.$route.params.id,
					groupId: this.$route.params.groupId
				}
			};
		},

		plural (string) {
			return pluralize(string);
		},

		edit () {
			this.$bvModal.show('add-edit-group');
		},

		deactivate () {
			this.$bvModal.msgBoxConfirm('Are you sure you want to deactivate this group? This action will disable all monitors, devices, and child groups for this group.', {
				title: 'Deactivate Group',
				headerBgVariant: 'danger',
				headerTextVariant: 'white',
				okVariant: 'danger',
				cancelVariant: 'link'
			})
				.then(result => {
					if (result) {
						this.$store.dispatch('groups/deactivate', this.group.id)
							.then((data) => {
								this.loadGroup(this.group.id);
							})
							.catch(err => {
								this.showErrorModal(err, 'Error Deactivating Group');
							});
					}
				})
				.catch(e => {
					this.showErrorModal(e);
				});
		},

		activate () {
			this.$bvModal.show('activate-group-modal');
		},

		closeDelete () {
			this.deleteGroupChoice = null;
		},

		deleteGroup () {
			const request = { id: this.group.id };

			if (this.deleteGroupChoice) {
				request.newGroup = this.deleteGroupChoice;
			}

			this.$store.dispatch('groups/deleteGroup', request)
				.then(data => {
					this.$store.commit('sidebar/REMOVE_GROUP', request);
					this.$router.push(`/clients/${this.client.id}`);
					this.$bvModal.msgBoxOk('Group successfully deleted.', {
						headerBgVariant: 'success',
						headerTextVariant: 'white',
						okVariant: 'success',
						title: 'Success'
					})
						.then(data => {
							this.$router.push(`/clients/${this.client.id}`);
						});
				});
		},

		closeActivate () {
			this.activateChildGroups = false;
			this.activateMonitors = false;
			this.activateDevices = false;
		},

		submitActivateGroup () {
			const request = {
				groupId: this.group.id
			};

			if (this.activateChildGroups) request.groups = true;
			if (this.activateMonitors) request.monitors = true;
			if (this.activateDevices) request.devices = true;

			this.$store.dispatch('groups/activate', request)
				.then(data => {
					this.loadGroup(this.group.id);
					this.activateMonitors = false;
					this.activateChildGroups = false;
					this.activateDevices = false;
					this.$bvModal.hide('activate-group-modal');
				})
				.catch(err => {
					this.showErrorModal(err, 'Error Activating Group');
				});
		},

		getBadGroups (group) {
			const id = parseInt(group.id);
			const idArray = [ id ];

			let resultArray = [].concat(idArray);

			const childGroups = this.client.groups.filter(element => parseInt(element.parent_group_id) === id);

			childGroups.forEach(element => {
				resultArray = resultArray.concat(this.getBadGroups(element));
			});

			return resultArray;
		},
		resourceNotFound () {
			this.notFound = true;
		},
		isManageSlug (slug) {
			return slug.includes('manage_client') ||
				slug === 'super_admin';
		}
	},

	computed: {
		groupId () {
			return parseInt(this.$route.params.groupId);
		},

		sidebarCollapsed () {
			return this.$store.getters['ui/appBarCollapsed'];
		},

		group () {
			return this.$store.getters['groups/current'];
		},

		client () {
			return this.$store.getters['clients/current'];
		},

		deleteGroupAllowedGroups () {
			const clientGroups = this.client.groups;
			const badGroupIds = this.getBadGroups(this.group);

			return clientGroups.filter(element => {
				return !badGroupIds.includes(parseInt(element.id));
			});
		},

		deleteGroupChoices () {
			const noParent = { value: null, text: 'Unassigned' };

			const groupList = this.deleteGroupAllowedGroups.slice();

			const mappedGroups = groupList.map(group => (
				{
					value: group.id,
					text: group.name
				}
			));

			mappedGroups.unshift(noParent);

			return mappedGroups;
		},
		allGroups () {
			return this.client.groups.slice();
		},
		clientGroups () {
			return this.allGroups.map(group => {
				const parent = this.client.groups.find(element => element.id === group.parent_group_id);
				group.parent = parent ? parent.name : 'None';
				return group;
			});
		},
		canView () {
			return this.$store.getters['auth/hasPermission']('view_client', true, this.client.id) ||
				this.$store.getters['auth/hasPermission']('manage_client*', true, this.client.id);
		},
		canManageSettings () {
			return this.$store.getters['auth/hasPermission']('manage_client_group', true, this.client.id);
		}
	},
	destroyed () {
		this.$store.commit('groups/current', null);
	}
};
</script>
